export const BETA_EDITOR_CANVAS_WIDTHS = {
    desktop: 1280,
    tablet: 768,
    mobile: 392,
} as const

export const BETA_EDITOR_SITE_CLIENT_BREAKPOINT = 768
export const BETA_EDITOR_PREVIEW_PATHNAME = '/page/preview'
export const BETA_EDITOR_PREVIEW_QUERY_PARAM = 'preview'

export type BetaEditorPreviewDevice = keyof typeof BETA_EDITOR_CANVAS_WIDTHS
export type BetaEditorPreviewSurface = 'editor' | 'site'

type PreviewUrlInput = {
    siteUrl?: string | null
    pagePreviewTarget?: string | number | null
    pageRoute?: string | number | null
}

const normalizeOptionalString = (value?: string | null) => {
    const normalizedValue = typeof value === 'string' ? value.trim() : ''

    return normalizedValue
}

const hasUsableSiteUrl = (siteUrl?: string | null) => {
    const normalizedSiteUrl = normalizeOptionalString(siteUrl).toLowerCase()

    return normalizedSiteUrl !== ''
        && normalizedSiteUrl !== 'undefined'
        && normalizedSiteUrl !== 'null'
}

const isAbsoluteUrl = (value: string) => (
    value.startsWith('http://') || value.startsWith('https://')
)

const getHostnameFromSiteUrl = (siteUrl: string) => {
    const normalizedSiteUrl = normalizeOptionalString(siteUrl)

    try {
        return new URL(isAbsoluteUrl(normalizedSiteUrl) ? normalizedSiteUrl : `http://${normalizedSiteUrl}`).hostname
    } catch {
        return ''
    }
}

const shouldUseHttpForLocalSiteClient = (siteUrl: string) => {
    const hostname = getHostnameFromSiteUrl(siteUrl)

    return hostname === 'localhost'
        || hostname === '127.0.0.1'
        || hostname.endsWith('.localhost')
        || hostname.endsWith('.uli.local')
}

const normalizeSiteUrl = (siteUrl: string) => {
    const normalizedSiteUrl = normalizeOptionalString(siteUrl)

    if (isAbsoluteUrl(normalizedSiteUrl)) {
        return shouldUseHttpForLocalSiteClient(normalizedSiteUrl)
            ? normalizedSiteUrl.replace(/^https:\/\//, 'http://')
            : normalizedSiteUrl
    }

    return `${shouldUseHttpForLocalSiteClient(normalizedSiteUrl) ? 'http' : 'https'}://${normalizedSiteUrl}`
}

const normalizePreviewTarget = (previewTarget?: string | number | null) => {
    if (previewTarget === null || previewTarget === undefined) return null

    const normalizedPreviewTarget = String(previewTarget).trim()
    const loweredPreviewTarget = normalizedPreviewTarget.toLowerCase()

    if (
        normalizedPreviewTarget === ''
        || normalizedPreviewTarget === '/'
        || loweredPreviewTarget === 'undefined'
        || loweredPreviewTarget === 'null'
    ) {
        return null
    }

    if (isAbsoluteUrl(normalizedPreviewTarget)) {
        const routePath = new URL(normalizedPreviewTarget).pathname
            .replace(/^\/+/, '')
            .replace(/\/+$/, '')

        return routePath || null
    }

    const routePath = normalizedPreviewTarget
        .split('?')[0]
        .split('#')[0]
        .trim()
        .replace(/^\/+/, '')
        .replace(/\/+$/, '')

    return routePath || null
}

const resolvePreviewTargetInput = ({ pagePreviewTarget, pageRoute }: PreviewUrlInput) => {
    const previewTarget = pagePreviewTarget ?? pageRoute

    return normalizePreviewTarget(previewTarget)
}

export const canBuildBetaEditorSitePreviewUrl = (input: PreviewUrlInput) => {
    const { siteUrl, pageRoute } = input

    if (!hasUsableSiteUrl(siteUrl)) return false

    const previewTarget = resolvePreviewTargetInput(input)
    if (!previewTarget) return false

    if (!pageRoute || typeof pageRoute !== 'string' || !isAbsoluteUrl(pageRoute)) return true

    const normalizedSiteUrlInput = normalizeOptionalString(siteUrl)

    try {
        const normalizedSiteUrl = new URL(normalizeSiteUrl(normalizedSiteUrlInput))
        const normalizedPageUrl = new URL(pageRoute)

        return normalizedSiteUrl.hostname === normalizedPageUrl.hostname
    } catch {
        return false
    }
}

export const buildBetaEditorSitePreviewUrl = (input: PreviewUrlInput) => {
    const { siteUrl } = input

    if (!hasUsableSiteUrl(siteUrl)) {
        throw new Error('siteUrl is required to build the beta editor preview url')
    }

    const previewTarget = resolvePreviewTargetInput(input)
    if (!previewTarget) {
        throw new Error('pagePreviewTarget is required to build the beta editor preview url')
    }

    const normalizedSiteUrlInput = normalizeOptionalString(siteUrl)
    const url = new URL(BETA_EDITOR_PREVIEW_PATHNAME, normalizeSiteUrl(normalizedSiteUrlInput))
    url.searchParams.set(BETA_EDITOR_PREVIEW_QUERY_PARAM, previewTarget)

    return url.toString()
}

export const resolveBetaEditorSitePreviewUrl = (input: PreviewUrlInput) => (
    canBuildBetaEditorSitePreviewUrl(input) ? buildBetaEditorSitePreviewUrl(input) : null
)
